/**
 * Centralized constants for API endpoints to ensure consistency and maintainability.
 */

const PROTECTED_BASE = "/protected";
const USER_BASE = `${PROTECTED_BASE}/users`;
const SUBSCRIPTIONS_BASE = `${PROTECTED_BASE}/subscriptions`;
const WALLET_BASE = `${PROTECTED_BASE}/wallet`;
const CHECKOUT_SESSIONS_BASE = `${PROTECTED_BASE}/checkoutSessions`;

const ADMIN_BASE = `${PROTECTED_BASE}/admin`;
const PARTNER_BASE = `${PROTECTED_BASE}/partner`;

// Muggle Test API Endpoints
const MUGGLE_TEST_BASE = `${PROTECTED_BASE}/muggle-test`;
const PROJECT_BASE = `${MUGGLE_TEST_BASE}/projects`;

export const API_ENDPOINTS = {
  PROTECTED_BASE: {
    BASE: PROTECTED_BASE,
  },
  MUGGLE_TEST: {
    USE_CASE_DISCOVERY_MEMORY: (projectId: string) => `${PROJECT_BASE}/${projectId}/use-case-discovery-memory`,
    USE_CASE_DISCOVERY_MEMORY_GRADUATIONS: (projectId: string) =>
      `${PROJECT_BASE}/${projectId}/use-case-discovery-memory/graduate`,
    PROJECT_SUMMARY_STATE: (projectId: string) => `${PROJECT_BASE}/${projectId}/summary-state`,
    PROJECT_SUMMARY_STATES_BATCH: `${PROJECT_BASE}/summary-states/batch`,
    SHORT_ID_RESOLUTION: (projectId: string, shortIdReference: string) =>
      `${PROJECT_BASE}/${projectId}/short-ids/${encodeURIComponent(shortIdReference)}`,
    SCRIPT_HISTORY: (testCaseId: string) => `${MUGGLE_TEST_BASE}/test-cases/${testCaseId}/script-history`,
    SCRIPT_HISTORY_RUNS: (testCaseId: string, recordingId: string) =>
      `${MUGGLE_TEST_BASE}/test-cases/${testCaseId}/script-history/${encodeURIComponent(recordingId)}/runs`,
    TEST_CASE_HEALTH_BATCH: (projectId: string) => `${PROJECT_BASE}/${projectId}/test-case-health/batch`,
    PROJECT_TRANSFER_CLAIM: `${MUGGLE_TEST_BASE}/project-transfers/claim`,
    PROJECT_TRANSFERS_RECEIVED: `${MUGGLE_TEST_BASE}/project-transfers/received`,
  },
  WALLET: {
    RECHARGE: `${WALLET_BASE}/topup`,
    PACKAGES: `${WALLET_BASE}/packages`,
    AUTO_TOPUP: `${WALLET_BASE}/auto-topup`,
    AUTO_TOPUP_PAYMENT_METHOD: `${WALLET_BASE}/auto-topup/payment-method`,
    PAYMENT_METHODS: `${WALLET_BASE}/payment-methods`,
    PAYMENT_METHODS_SETUP: `${WALLET_BASE}/payment-methods/setup`,
    PAYMENT_METHOD_BY_ID: (paymentMethodId: string) => `${WALLET_BASE}/payment-methods/${paymentMethodId}`,
    TRANSACTIONS: `${WALLET_BASE}/transactions`,
  },
  PROFILE: {
    ROOT: `/users/profile`,
    AVATAR: `/users/avatar`,
  },
  STORAGE: {
    PUBLIC_URL: `${PROTECTED_BASE}/storage/publicUrl`,
  },
  API_KEYS: {
    ROOT: `${PROTECTED_BASE}/api-keys`,
    BY_ID: (id: string) => `${PROTECTED_BASE}/api-keys/${id}`,
  },
  USER: {
    AUTH_TOKENS: `${PROTECTED_BASE}/auth/tokens`,
    PROFILE: `${USER_BASE}/profile`,
    QUOTA: `${USER_BASE}/quota`,
    SYNC_STRIPE: `${USER_BASE}/sync/stripe`,
    PASSWORD_CHANGE: `${USER_BASE}/password-change`,
  },
  CHECKOUT_SESSIONS: {
    VERIFICATION: `${CHECKOUT_SESSIONS_BASE}/verification`,
  },
  SUBSCRIPTIONS: {
    ROOT: `${SUBSCRIPTIONS_BASE}`,
    UPDATE: `${SUBSCRIPTIONS_BASE}/update`,
    STATUS: `${SUBSCRIPTIONS_BASE}/status`,
    PLANS: `${SUBSCRIPTIONS_BASE}/plans`,
  },
  PARTNER: {
    ME: `${PARTNER_BASE}/me`,
    AGREEMENT: `${PARTNER_BASE}/agreement`,
    INVITES: `${PARTNER_BASE}/invites`,
    REFERRALS: `${PARTNER_BASE}/referrals`,
    REFERRAL_PAYMENTS: (referralId: string) => `${PARTNER_BASE}/referrals/${encodeURIComponent(referralId)}/payments`,
    STATEMENTS: `${PARTNER_BASE}/statements`,
  },
  ADMIN: {
    USERS: `${ADMIN_BASE}/users`,
    USER_QUOTA: (userId: string) => `${ADMIN_BASE}/users/${userId}/quota`,
    GRANT_TRIAL: `${ADMIN_BASE}/accounts/trial`,
    ACCOUNT_TOPUP: `${ADMIN_BASE}/accounts/topup`,
    SUBSCRIPTIONS_USERS: `${ADMIN_BASE}/subscriptions/users`,
    SUBSCRIPTIONS_ORGANIZATIONS: `${ADMIN_BASE}/subscriptions/organizations`,
    PROJECT_TRANSFERS: `${ADMIN_BASE}/project-transfers`,
    PROJECT_TRANSFER_PREVIEW: `${ADMIN_BASE}/project-transfers/preview`,
    PROJECT_TRANSFER_RESEND: (transferId: string) => `${ADMIN_BASE}/project-transfers/${transferId}/resend`,
    PROJECT_TRANSFER_CANCEL: (transferId: string) => `${ADMIN_BASE}/project-transfers/${transferId}/cancel`,
    PRICING_CASES: `${ADMIN_BASE}/pricing-cases`,
    PRICING_CASE_STATUS: (caseId: string) => `${ADMIN_BASE}/pricing-cases/${caseId}/status`,
    PARTNER_PROGRAM: {
      PARTNERS: `${ADMIN_BASE}/partners`,
      PARTNER: (partnerUserId: string) => `${ADMIN_BASE}/partners/${encodeURIComponent(partnerUserId)}`,
      PARTNER_STATUS: (partnerUserId: string) => `${ADMIN_BASE}/partners/${encodeURIComponent(partnerUserId)}/status`,
      REFERRALS: `${ADMIN_BASE}/referrals`,
      REFERRAL_READDRESS: (referralId: string) => `${ADMIN_BASE}/referrals/${encodeURIComponent(referralId)}/readdress`,
      REFERRAL_REVOKE: (referralId: string) => `${ADMIN_BASE}/referrals/${encodeURIComponent(referralId)}/revoke`,
      STATEMENTS: `${ADMIN_BASE}/partner-statements`,
      STATEMENT_RELEASE: (statementId: string) =>
        `${ADMIN_BASE}/partner-statements/${encodeURIComponent(statementId)}/release`,
      STATEMENT_APPROVE: (statementId: string) =>
        `${ADMIN_BASE}/partner-statements/${encodeURIComponent(statementId)}/approve`,
      STATEMENT_PAYOUT: (statementId: string) =>
        `${ADMIN_BASE}/partner-statements/${encodeURIComponent(statementId)}/payout`,
      SOURCE_OF_SALES: `${ADMIN_BASE}/partner-reports/source-of-sales`,
    },
  },
  ORGANIZATION_PRICING: {
    PRICING_CASES: (organizationId: string) => `${PROTECTED_BASE}/organizations/${organizationId}/pricing-cases`,
    PRICE_PULSES: (organizationId: string) => `${PROTECTED_BASE}/organizations/${organizationId}/price-pulses`,
    LATEST_PRICE_PULSE: (organizationId: string) =>
      `${PROTECTED_BASE}/organizations/${organizationId}/price-pulses/latest`,
  },
} as const;
